// components/common/Comparateur.js
// Gestion de la sélection et du panneau de comparaison côte à côte
import React, { useState, createContext, useContext, useCallback } from 'react';
import { useCurrency } from '../../utils/currency';
import './Comparateur.css';

const ComparateurContext = createContext(null);
const MAX = 3;

export function ComparateurProvider({ children }) {
  const [selection, setSelection] = useState([]); // array of annonce objects
  const [open, setOpen] = useState(false);

  const toggle = useCallback((annonce) => {
    setSelection(prev => {
      const already = prev.find(a => a.id === annonce.id);
      if (already) return prev.filter(a => a.id !== annonce.id);
      if (prev.length >= MAX) return prev; // max 3
      return [...prev, annonce];
    });
  }, []);

  const isSelected = useCallback((id) => selection.some(a => a.id === id), [selection]);

  const clear = useCallback(() => { setSelection([]); setOpen(false); }, []);

  return (
    <ComparateurContext.Provider value={{ selection, toggle, isSelected, open, setOpen, clear }}>
      {children}
      {selection.length > 0 && <ComparateurBar />}
      {open && <ComparateurPanel />}
    </ComparateurContext.Provider>
  );
}

export function useComparateur() {
  return useContext(ComparateurContext);
}

/* ── Barre flottante ── */
function ComparateurBar() {
  const { selection, setOpen, clear } = useComparateur();

  return (
    <div className="comp-bar">
      <div className="comp-bar-inner">
        <div className="comp-bar-items">
          {selection.map(a => (
            <div key={a.id} className="comp-bar-item">
              <img src={a.photo} alt={a.titre} />
              <span>{a.titre.substring(0, 22)}{a.titre.length > 22 ? '…' : ''}</span>
            </div>
          ))}
          {Array.from({ length: MAX - selection.length }).map((_, i) => (
            <div key={`empty-${i}`} className="comp-bar-item comp-bar-empty">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
              <span>Ajouter</span>
            </div>
          ))}
        </div>
        <div className="comp-bar-actions">
          <span className="comp-bar-count">{selection.length} / {MAX}</span>
          <button
            className="comp-bar-btn"
            onClick={() => setOpen(true)}
            disabled={selection.length < 2}
          >
            Comparer
          </button>
          <button className="comp-bar-clear" onClick={clear} title="Vider la sélection">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Panneau de comparaison ── */
const ROWS = [
  { label: 'Type',           key: 'type' },
  { label: 'Prix',           key: 'prix',     fmt: true },
  { label: 'Prix / m²',     key: '_prixM2',  computed: true },
  { label: 'Surface',       key: 'surface',  suffix: 'm²' },
  { label: 'Chambres',      key: 'chambres' },
  { label: 'Salles de bain',key: 'sallesEau' },
  { label: 'Wilaya',        key: 'wilaya' },
  { label: 'Commune',       key: 'commune' },
  { label: 'Quartier',      key: 'quartier' },
];

function ComparateurPanel() {
  const { selection, setOpen, clear } = useComparateur();
  const { formatPrix } = useCurrency();

  const fmt = (annonce, row) => {
    if (row.computed && row.key === '_prixM2') {
      const s = Number(annonce.surface);
      const p = Number(annonce.prix);
      if (!s || !p) return '—';
      return `${Math.round(p / s).toLocaleString('fr-DZ')} DA/m²`;
    }
    const v = annonce[row.key];
    if (v === undefined || v === null || v === '') return '—';
    if (row.fmt) return formatPrix(Number(v));
    if (row.suffix) return `${v} ${row.suffix}`;
    return String(v);
  };

  // Highlight best price (lowest) et best prix/m²
  const prices = selection.map(a => Number(a.prix));
  const minPrice = Math.min(...prices);

  const prixM2s = selection.map(a => {
    const s = Number(a.surface), p = Number(a.prix);
    return s && p ? Math.round(p / s) : Infinity;
  });
  const minPrixM2 = Math.min(...prixM2s);

  return (
    <div className="comp-overlay" onClick={() => setOpen(false)}>
      <div className="comp-panel" onClick={e => e.stopPropagation()}>
        <div className="comp-panel-header">
          <h3>Comparaison côte à côte</h3>
          <button className="comp-panel-close" onClick={() => setOpen(false)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="comp-table-wrap">
          <table className="comp-table">
            <thead>
              <tr>
                <th className="comp-label-col"></th>
                {selection.map(a => (
                  <th key={a.id}>
                    <div className="comp-th-content">
                      <img src={a.photo} alt={a.titre} className="comp-th-img" />
                      <span className="comp-th-title">{a.titre}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(row => (
                <tr key={row.key}>
                  <td className="comp-label">{row.label}</td>
                  {selection.map(a => {
                    const isPrix   = row.key === 'prix';
                    const isPrixM2 = row.key === '_prixM2';
                    const s = Number(a.surface), p = Number(a.prix);
                    const thisM2  = s && p ? Math.round(p / s) : Infinity;
                    const isBest  =
                      (isPrix   && Number(a.prix) === minPrice) ||
                      (isPrixM2 && thisM2 === minPrixM2 && minPrixM2 !== Infinity);
                    return (
                      <td key={a.id} className={`comp-val${isBest ? ' comp-best' : ''}`}>
                        {fmt(a, row)}
                        {isBest && <span className="comp-best-badge">Meilleur prix</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="comp-panel-footer">
          <button className="comp-clear-btn" onClick={clear}>Vider la comparaison</button>
        </div>
      </div>
    </div>
  );
}
